import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Search, Plus, MoreVertical, X } from "lucide-react";
import { Checkbox } from "@mui/material";
import ModalV2 from "../../../shared/components/modalV2";
import SuccessModalV2 from "../../../shared/components/successModal/sucessModalV2";
import WarningPromptModal from "../registrar2/case-folder/modals/WarningPromptModal";
import { GREEN_CLOSE_BTN } from "../../../assets";

type StaffRole =
  | "Court Registrar"
  | "Court Secretary"
  | "Court Clerk"
  | "Court Reporter";

type CourtStaffItem = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: StaffRole;
  date: string;
};

const ROLE_OPTIONS: StaffRole[] = [
  "Court Registrar",
  "Court Secretary",
  "Court Clerk",
  "Court Reporter",
];

type AddStaffModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (staff: Omit<CourtStaffItem, "id" | "date">) => void;
};

const AddStaffModal: React.FC<AddStaffModalProps> = ({
  isOpen,
  onClose,
  onAdd,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole | "">("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !role) {
      return;
    }

    onAdd({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      role,
    });

    setFirstName("");
    setLastName("");
    setEmail("");
    setRole("");
  };

  return (
    <ModalV2
      isOpen={isOpen}
      isClose={onClose}
      maxWidth="w-[458px]"
      edges="rounded-2xl"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full px-8 py-8 flex flex-col gap-y-5 relative"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 text-[#22C55E]"
        >
          <img src={GREEN_CLOSE_BTN} alt="Close" className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-bold text-[#1A1A1A] text-center mb-2">
          Add a new Court staff
        </h2>

        <div className="flex flex-col gap-y-3">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#00B074]"
          />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#00B074]"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#00B074]"
          />

          <div className="relative">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as StaffRole | "")}
              className="w-full appearance-none px-4 py-3 border border-[#E9EAEB] rounded-lg text-sm text-[#101828] focus:outline-none focus:border-[#00B074] bg-white"
            >
              <option value="">Role</option>
              {ROLE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[#667085]">
              ▾
            </span>
          </div>
        </div>

        <button
          type="submit"
          className="mt-2 w-full bg-[#00B074] text-white rounded-lg py-3 text-sm font-medium hover:bg-[#009060] transition-colors"
        >
          Add
        </button>
      </form>
    </ModalV2>
  );
};

type ChangeRoleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentRole?: StaffRole;
  onConfirm: (role: StaffRole) => void;
};

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  onClose,
  currentRole,
  onConfirm,
}) => {
  const [selectedRole, setSelectedRole] = useState<StaffRole | "">(
    currentRole || "",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) return;
    onConfirm(selectedRole);
  };

  return (
    <ModalV2
      isOpen={isOpen}
      isClose={onClose}
      maxWidth="w-[448px]"
      edges="rounded-xl"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full px-8 py-8 flex flex-col gap-y-6 relative"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 text-[#101828] hover:text-[#111827]"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-xl font-bold text-[#1A1A1A] text-center">
          Change Role
        </h2>

        <div className="relative">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as StaffRole | "")}
            className="w-full appearance-none px-4 py-3 border border-[#E9EAEB] rounded-lg text-sm text-[#101828] focus:outline-none focus:border-[#00B074] bg-white"
          >
            <option value="">Select role</option>
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[#667085]">
            ▾
          </span>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-[#E5E5E5] rounded-lg py-3 text-[16px] font-medium text-[#515457]"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-full bg-[#00B074] rounded-lg py-3 text-[16px] font-medium text-white hover:bg-[#009060] transition-colors"
          >
            Ok
          </button>
        </div>
      </form>
    </ModalV2>
  );
};

const CourtStaff: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [staff, setStaff] = useState<CourtStaffItem[]>([
    {
      id: 1,
      firstName: "Esther",
      lastName: "Ochendo",
      email: "Esthero@gmail.com",
      role: "Court Registrar",
      date: "May 3, 2024",
    },
    {
      id: 2,
      firstName: "Esther",
      lastName: "Ochendo",
      email: "Esthero@gmail.com",
      role: "Court Secretary",
      date: "May 3, 2024",
    },
    {
      id: 3,
      firstName: "Esther",
      lastName: "Ochendo",
      email: "Esthero@gmail.com",
      role: "Court Clerk",
      date: "May 3, 2024",
    },
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showChangeRoleSuccess, setShowChangeRoleSuccess] = useState(false);
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);
  const [showRemoveSuccess, setShowRemoveSuccess] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<CourtStaffItem | null>(
    null,
  );

  const filteredStaff = useMemo(() => {
    if (!searchTerm.trim()) return staff;
    const term = searchTerm.toLowerCase();
    return staff.filter((item) => {
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      return (
        fullName.includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.role.toLowerCase().includes(term)
      );
    });
  }, [staff, searchTerm]);

  const handleAddStaff = (payload: Omit<CourtStaffItem, "id" | "date">) => {
    const newStaff: CourtStaffItem = {
      id: Date.now(),
      ...payload,
      date: "May 3, 2024",
    };
    setStaff((prev) => [...prev, newStaff]);
    setShowAddModal(false);
    setShowAddSuccess(true);
  };

  const handleOpenChangeRole = (item: CourtStaffItem) => {
    setSelectedStaff(item);
    setShowChangeRoleModal(true);
  };

  const handleConfirmChangeRole = (role: StaffRole) => {
    if (!selectedStaff) return;
    setStaff((prev) =>
      prev.map((item) =>
        item.id === selectedStaff.id ? { ...item, role } : item,
      ),
    );
    setShowChangeRoleModal(false);
    setShowChangeRoleSuccess(true);
  };

  const handleOpenRemove = (item: CourtStaffItem) => {
    setSelectedStaff(item);
    setShowRemoveWarning(true);
  };

  const handleConfirmRemove = () => {
    if (!selectedStaff) return;
    setStaff((prev) => prev.filter((item) => item.id !== selectedStaff.id));
    setShowRemoveWarning(false);
    setShowRemoveSuccess(true);
    setSelectedStaff(null);
  };

  const hasStaff = filteredStaff.length > 0;

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white px-8 pt-6 pb-0 border-b border-[#E9EAEB]">
        <div className="flex items-center justify-between mb-4 ">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">Court staff</h1>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827] shadow-sm hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
            <span>Add a new Court staff</span>
          </button>
        </div>
        <div className="flex items-center gap-x-2 text-sm text-[#464255] mb-4">
          <Link to="/panel/overview" className="hover:text-[#00B074]">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-medium text-[#1A1A1A]">Court staff</span>
        </div>
      </div>

      <div className="px-8 py-6">
        <div className="relative w-full max-w-[320px]">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#667085]"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]" />
        </div>
      </div>

      {hasStaff ? (
        <div className="flex-1 px-8 pb-10">
          <div className="bg-white rounded-2xl overflow-hidden">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-[#F9FAFB] text-xs font-medium text-[#667085]">
                  <th className="px-6 py-3 text-left">
                    <Checkbox
                      color="success"
                      sx={{
                        color: "#E0E0E0",
                        "&.Mui-checked": {
                          color: "#00B074",
                        },
                      }}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Email</th>
                  <th className="px-6 py-3 text-left">Role</th>
                  <th className="px-6 py-3 text-left">Details</th>
                  <th className="px-6 py-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((item) => (
                  <tr
                    key={item.id}
                    className="text-sm text-[#111827] border-b border-[#E5E7EB] last:border-b-0"
                  >
                    <td className="px-6 py-4 align-top">
                      <Checkbox
                        color="success"
                        sx={{
                          color: "#E0E0E0",
                          "&.Mui-checked": {
                            color: "#00B074",
                          },
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 align-top">
                      {item.firstName} {item.lastName}
                    </td>
                    <td className="px-6 py-4 align-top">{item.email}</td>
                    <td className="px-6 py-4 align-top">{item.role}</td>
                    <td className="px-6 py-4 align-top">{item.date}</td>
                    <td className="px-6 py-4 align-top">
                      <Menu
                        as="div"
                        className="relative inline-block text-left"
                      >
                        <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
                          <MoreVertical className="w-5 h-5" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                            <div className="px-1 py-1 flex flex-col gap-y-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenChangeRole(item)}
                                    className={`${
                                      active
                                        ? "bg-[#E6F7F1] text-[#027A48]"
                                        : "text-[#344054]"
                                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                                  >
                                    Change role
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenRemove(item)}
                                    className={`${
                                      active
                                        ? "bg-[#E6F7F1] text-[#027A48]"
                                        : "text-[#344054]"
                                    } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium`}
                                  >
                                    Remove user
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E7EB] text-sm text-[#6B7280]">
              <span>
                Page <span className="font-medium">1</span> of{" "}
                <span className="font-medium">10</span>
              </span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 rounded-lg border border-[#D1D5DB] bg-white hover:bg-gray-50">
                    Previous
                  </button>
                  <button className="px-3 py-1.5 rounded-lg border border-[#D1D5DB] bg-white hover:bg-gray-50">
                    Next
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span>Entries per page:</span>
                  <select className="border border-[#D1D5DB] rounded-lg px-2 py-1 bg-white focus:outline-none text-sm">
                    <option>10</option>
                    <option>20</option>
                    <option>50</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 px-8 pb-10 flex items-center justify-center">
          <div className="bg-white border border-[#E9EAEB] rounded-2xl px-10 py-12 text-center max-w-md w-full">
            <p className="text-base font-medium text-[#101828] mb-2">
              No court staff added yet
            </p>
            <p className="text-sm text-[#667085] mb-6">
              Add a new court staff to see them listed here.
            </p>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827] shadow-sm hover:bg-gray-50"
            >
              <Plus className="w-4 h-4" />
              <span>Add a new Court staff</span>
            </button>
          </div>
        </div>
      )}

      <AddStaffModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddStaff}
      />
      <SuccessModalV2
        open={showAddSuccess}
        close={() => setShowAddSuccess(false)}
        message="Staff added successfully"
      />

      <ChangeRoleModal
        isOpen={showChangeRoleModal}
        onClose={() => setShowChangeRoleModal(false)}
        currentRole={selectedStaff?.role}
        onConfirm={handleConfirmChangeRole}
      />
      <SuccessModalV2
        open={showChangeRoleSuccess}
        close={() => setShowChangeRoleSuccess(false)}
        message="Role changed successfully"
      />

      <WarningPromptModal
        isOpen={showRemoveWarning}
        onClose={() => setShowRemoveWarning(false)}
        message="Are you sure you want to remove Court staff?"
        onClick={handleConfirmRemove}
      />
      <SuccessModalV2
        open={showRemoveSuccess}
        close={() => setShowRemoveSuccess(false)}
        message="Court staff removed successfully"
      />
    </div>
  );
};

export default CourtStaff;
